/**
 * Sprint 3.1.5 — Admin capability & navigation registry (expandable; no runtime auth here).
 * Server still enforces requireAdminSession on every API.
 */

/** @typedef {{ id: string, labelKey: string, path: string, capability: string, navExact?: boolean }} AdminNavItem */

/** @typedef {{ id: string, titleKey: string, items: AdminNavItem[] }} AdminNavGroup */

/** @type {Record<string, { descriptionKey: string }>} */
export const ADMIN_CAPABILITY_REGISTRY = {
  overview: { descriptionKey: 'adminHub.capOverview' },
  commercial_plans: { descriptionKey: 'adminHub.capCommercialPlans' },
  commercial_entitlements: { descriptionKey: 'adminHub.capCommercialEntitlements' },
  commercial_guest: { descriptionKey: 'adminHub.capCommercialGuest' },
  commercial_firm: { descriptionKey: 'adminHub.capCommercialFirm' },
  commercial_ai: { descriptionKey: 'adminHub.capCommercialAi' },
  commercial_enforcement: { descriptionKey: 'adminHub.capCommercialEnforcement' },
  ops_insights: { descriptionKey: 'adminHub.capOpsInsights' },
  ops_health: { descriptionKey: 'adminHub.capOpsHealth' },
  ops_async: { descriptionKey: 'adminHub.capOpsAsync' },
  org_offices: { descriptionKey: 'adminHub.capOrgOffices' },
  platform_flags: { descriptionKey: 'adminHub.capPlatformFlags' },
  platform_auth_login: { descriptionKey: 'adminHub.capPlatformAuthLogin' },
  platform_readiness: { descriptionKey: 'adminHub.capPlatformReadiness' },
  platform_observability: { descriptionKey: 'adminHub.capPlatformObservability' },
  commercial_snapshot: { descriptionKey: 'adminHub.capCommercialSnapshot' },
};

/** @type {AdminNavGroup[]} */
export const ADMIN_NAV_GROUPS = [
  {
    id: 'overview',
    titleKey: 'adminHub.navOverview',
    items: [
      {
        id: 'legacy-dashboard',
        labelKey: 'adminHub.navLegacyDashboard',
        path: '/admin',
        capability: 'overview',
        navExact: true,
      },
    ],
  },
  {
    id: 'commercial',
    titleKey: 'adminHub.navCommercial',
    items: [
      { id: 'plans', labelKey: 'adminHub.navPlans', path: '/admin/commercial/plans', capability: 'commercial_plans' },
      {
        id: 'entitlements',
        labelKey: 'adminHub.navEntitlements',
        path: '/admin/commercial/entitlements',
        capability: 'commercial_entitlements',
      },
      {
        id: 'guest',
        labelKey: 'adminHub.navGuestPolicies',
        path: '/admin/commercial/guest-policies',
        capability: 'commercial_guest',
      },
      { id: 'firm', labelKey: 'adminHub.navFirmMode', path: '/admin/commercial/firm-mode', capability: 'commercial_firm' },
      {
        id: 'ai',
        labelKey: 'adminHub.navAiUsagePolicies',
        path: '/admin/commercial/ai-usage',
        capability: 'commercial_ai',
      },
      {
        id: 'enforcement',
        labelKey: 'adminHub.navEnforcementReadiness',
        path: '/admin/commercial/enforcement-readiness',
        capability: 'commercial_enforcement',
      },
      {
        id: 'commercial-snapshot',
        labelKey: 'adminHub.navCommercialSnapshot',
        path: '/admin/commercial/snapshot',
        capability: 'commercial_snapshot',
      },
    ],
  },
  {
    id: 'operations',
    titleKey: 'adminHub.navOperations',
    items: [
      {
        id: 'ai-visibility',
        labelKey: 'adminHub.navAiVisibility',
        path: '/admin/operations/ai-visibility',
        capability: 'ops_insights',
      },
      {
        id: 'op-health',
        labelKey: 'adminHub.navOperationalHealth',
        path: '/admin/operations/operational-health',
        capability: 'ops_health',
      },
      {
        id: 'async',
        labelKey: 'adminHub.navAsyncJobs',
        path: '/admin/operations/async-jobs',
        capability: 'ops_async',
      },
    ],
  },
  {
    id: 'organizations',
    titleKey: 'adminHub.navOrganizations',
    items: [
      {
        id: 'offices',
        labelKey: 'adminHub.navOfficesConsumption',
        path: '/admin/organizations/offices',
        capability: 'org_offices',
      },
    ],
  },
  {
    id: 'platform',
    titleKey: 'adminHub.navPlatform',
    items: [
      {
        id: 'flags',
        labelKey: 'adminHub.navFeatureFlags',
        path: '/admin/platform/feature-flags',
        capability: 'platform_flags',
      },
      {
        id: 'auth-login',
        labelKey: 'adminHub.navAuthLogin',
        path: '/admin/platform/auth-login',
        capability: 'platform_auth_login',
      },
      {
        id: 'readiness',
        labelKey: 'adminHub.navServiceReadiness',
        path: '/admin/platform/readiness',
        capability: 'platform_readiness',
      },
      {
        id: 'observability',
        labelKey: 'adminHub.navObservability',
        path: '/admin/observability',
        capability: 'platform_observability',
      },
    ],
  },
];
